/**
 * Global market-language filter — shared across pages, persisted per wallet.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAccount } from 'wagmi';
import { loadLangPrefForScope, saveLangPrefForScope } from '../lib/languagePreference';

interface LanguagePreferenceContextValue {
  langFilter: string | null;
  setLangFilter: (lang: string | null) => void;
}

const LanguagePreferenceContext = createContext<LanguagePreferenceContextValue | null>(null);

export function LanguagePreferenceProvider({ children }: { children: React.ReactNode }) {
  const { address } = useAccount();
  const scopeKey = address?.toLowerCase() ?? null;
  const [langFilter, setLangFilterState] = useState<string | null>(() => loadLangPrefForScope(scopeKey));
  const prevScopeRef = useRef<string | null>(scopeKey);

  useEffect(() => {
    if (prevScopeRef.current === scopeKey) return;
    prevScopeRef.current = scopeKey;
    setLangFilterState(loadLangPrefForScope(scopeKey));
  }, [scopeKey]);

  const setLangFilter = useCallback(
    (lang: string | null) => {
      const normalized = lang != null && lang !== '' ? String(lang).trim().toLowerCase() : null;
      setLangFilterState(normalized);
      saveLangPrefForScope(scopeKey, normalized);
    },
    [scopeKey],
  );

  return (
    <LanguagePreferenceContext.Provider value={{ langFilter, setLangFilter }}>
      {children}
    </LanguagePreferenceContext.Provider>
  );
}

export function useLanguagePreference() {
  const ctx = useContext(LanguagePreferenceContext);
  if (!ctx) {
    return { langFilter: null as string | null, setLangFilter: () => {} };
  }
  return ctx;
}
